import { Card, Select } from 'antd';
import React, { useState } from 'react';
import { ClientPortfolio } from 'api/useClient';

/**
 * @typedef {import('react').JSX.Element} JSXElement
 */

/**
 * Props for the PortfolioSelector component
 */
interface PortfolioSelectorProps {
  /** Array of client portfolio data with hierarchical structure */
  clientPortfolio: ClientPortfolio[];
  /** Callback function called when a selection is made, receives the selected client ID or null */
  onSelection: (client_id: number | null) => void;
  /** Optional custom body style for the Card component */
  bodyStyle?: React.CSSProperties;
  /** Optional custom className for the Card component */
  className?: string;
}

/**
 * Portfolio selector component that allows users to select from a hierarchical client portfolio structure.
 * Displays two dropdown selectors: one for root companies (GP) and one for their subsidiaries (PortCo).
 * When a root company is selected, the subsidiary dropdown is populated with its children.
 * @param {PortfolioSelectorProps} props - The component props
 * @param {ClientPortfolio[]} props.clientPortfolio - Array of client portfolio data with hierarchical structure
 * @param {(client_id: number | null) => void} props.onSelection - Callback function called when a selection is made
 * @param {React.CSSProperties} [props.bodyStyle] - Optional custom body style for the Card component
 * @param {string} [props.className] - Optional custom className for the Card component
 * @returns {JSXElement} JSX element containing the portfolio selector interface
 */
export const PortfolioSelector: React.FC<PortfolioSelectorProps> = ({
  clientPortfolio,
  onSelection,
  bodyStyle,
  className
}: PortfolioSelectorProps) => {
  const [selectedRootId, setSelectedRootId] = useState<number | null>(null);

  const handleRootSelection = (value: number | null | undefined) => {
    const nextRootId: number | null = value ?? null;
    setSelectedRootId(nextRootId);
    onSelection(nextRootId);
  };

  // Check if any portfolio item has children
  const hasChildren = clientPortfolio.some((company) => company.children.length > 0);

  // If no children then show nothing
  if (!hasChildren) return null;
  return (
    <Card
      size="small"
      bordered={false}
      bodyStyle={bodyStyle || { backgroundColor: '#fbfbfb' }}
      className={className || 'mb-0 border-b border-gray-700'}
    >
      <div className="pl-3">
        <div className="text-xs text-gray-500 ml-1 mb-2">Portfolio View</div>
        <div className="flex gap-4">
          <Select
            placeholder="Select GP"
            style={{ width: 240 }}
            value={selectedRootId}
            size="middle"
            onChange={handleRootSelection}
            allowClear
            options={clientPortfolio.map((company) => ({
              value: company.id,
              label: company.name
            }))}
          />
        </div>
      </div>
    </Card>
  );
};
